import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { partition } from 'lodash-es';
import { FileType, useAddCaseFilesMutation, useDeleteCaseFileMutation } from '@/queries/file';
import { TMDXFile } from '@/api/apis/files';
import { retrieveUnifiedError } from '@/api/utils';
import { useUploadFile } from './useUploadFile';

export const useUpdateCaseFiles = () => {
  const { uploadFiles } = useUploadFile(FileType.Case);
  const { mutateAsync: attachFilesMutation } = useAddCaseFilesMutation();
  const { mutateAsync: deleteFileMutation } = useDeleteCaseFileMutation();

  // Attach moves the upload out of the temp bucket under a freshly generated file id, so only the case it
  // answers with tells us what the file is now called.
  const uploadFilesToCase = useCallback(
    async (newFiles: TMDXFile[], caseId: string) => {
      const uploadedFiles = await uploadFiles(newFiles);
      const updatedCase = await attachFilesMutation({ caseId, files: uploadedFiles });
      return updatedCase?.files;
    },
    [attachFilesMutation, uploadFiles],
  );

  const removeFilesFromCase = useCallback(
    async (deletedFiles: TMDXFile[], caseId: string) => {
      let caseFiles: TMDXFile[] | undefined;

      // eslint-disable-next-line no-restricted-syntax
      for await (const file of deletedFiles) {
        try {
          const updatedCase = await deleteFileMutation({ caseId, fileId: file.id! });
          caseFiles = updatedCase?.files ?? caseFiles;
        } catch (e) {
          const error = retrieveUnifiedError(e);
          toast.error(error.message);
        }
      }

      return caseFiles;
    },
    [deleteFileMutation],
  );

  /** Returns the case's own file list, so the caller can keep its state (form values, diff baseline) on the
   * ids the BE generated rather than on the throwaway ones the upload produced. */
  return useCallback(
    async (prevFiles: TMDXFile[], nextFiles: TMDXFile[], caseId: string): Promise<TMDXFile[]> => {
      const [existingFiles, newFiles] = partition(nextFiles, (f) =>
        prevFiles.some((existingFile) => existingFile.id === f.id),
      );
      const deletedFiles = prevFiles.filter((f) => !existingFiles.some((uf) => uf.id === f.id));

      const afterRemove = deletedFiles.length ? await removeFilesFromCase(deletedFiles, caseId) : undefined;
      const afterUpload = newFiles.length ? await uploadFilesToCase(newFiles, caseId) : undefined;

      return afterUpload ?? afterRemove ?? nextFiles;
    },
    [removeFilesFromCase, uploadFilesToCase],
  );
};
